import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, User as UserIcon } from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import { BLOG_POSTS, findPostBySlug } from '../data/blog';
import { useSeo, OG_IMAGE } from '../lib/seo';

function useArticleJsonLd(post) {
  useEffect(() => {
    if (!post) return;
    const data = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.metaDescription || post.excerpt,
      image: post.img,
      datePublished: post.datePublished,
      author: { '@type': 'Organization', name: post.author },
      publisher: {
        '@type': 'Organization',
        name: 'A+ Auto Glass',
        logo: {
          '@type': 'ImageObject',
          url: 'https://customer-assets.emergentagent.com/job_glass-quote-hub-1/artifacts/6r70037d_auto%20glass.jpg',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${window.location.origin}/blog/${post.slug}`,
      },
      articleSection: post.category,
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-testid', 'blog-post-jsonld');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [post]);
}

function renderBlock(block, idx) {
  switch (block.type) {
    case 'h2':
      return (
        <h2
          key={idx}
          className="mt-12 mb-4 text-[26px] md:text-[30px] font-black text-[#111827] tracking-[-0.01em] leading-[1.2]"
        >
          {block.text}
        </h2>
      );
    case 'h3':
      return (
        <h3
          key={idx}
          className="mt-8 mb-3 text-[18px] md:text-[20px] font-black text-[#111827] tracking-tight leading-snug"
        >
          {block.text}
        </h3>
      );
    case 'p':
      return (
        <p key={idx} className="mt-5 text-[16px] text-[#3f4653] leading-[1.8]">
          {block.text}
        </p>
      );
    case 'ul':
      return (
        <ul key={idx} className="mt-5 space-y-2.5 pl-5">
          {block.items.map((item, i) => (
            <li key={i} className="text-[15.5px] text-[#3f4653] leading-[1.75] list-disc marker:text-[#0D5BD7]">
              {item}
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={idx} className="mt-5 space-y-2.5 pl-5">
          {block.items.map((item, i) => (
            <li key={i} className="text-[15.5px] text-[#3f4653] leading-[1.75] list-decimal marker:text-[#0D5BD7] marker:font-bold">
              {item}
            </li>
          ))}
        </ol>
      );
    case 'quote':
      return (
        <blockquote
          key={idx}
          className="mt-8 mb-2 border-l-4 border-[#0D5BD7] pl-6 py-1 text-[17px] md:text-[19px] font-medium text-[#111827] italic leading-[1.6]"
        >
          “{block.text}”
        </blockquote>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPostBySlug(slug);

  useSeo({
    title: post
      ? `${post.title} | A+ Auto Glass Insights`
      : 'Article Not Found | A+ Auto Glass',
    description: post ? post.metaDescription || post.excerpt : 'The article you are looking for was not found.',
    path: post ? `/blog/${post.slug}` : '/blog',
    image: post?.img || OG_IMAGE,
    type: 'article',
  });
  useArticleJsonLd(post);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white text-[#111827]" data-testid={`blog-post-page-${post.slug}`}>
      <Navbar />

      {/* HERO */}
      <section className="bg-[#0a1220] text-white">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-14 md:py-20">
          <div className="text-[11px] font-bold tracking-[0.28em] text-[#7fb0ff]">
            <Link to="/" className="hover:text-white transition-colors">
              HOME
            </Link>
            <span className="mx-2 text-white/50">/</span>
            <Link to="/blog" className="hover:text-white transition-colors">
              BLOG
            </Link>
            <span className="mx-2 text-white/50">/</span>
            <span className="text-white truncate">{post.category.toUpperCase()}</span>
          </div>
          <div className="mt-5 text-[11px] font-bold tracking-[0.22em] text-[#7fb0ff]">
            {post.category.toUpperCase()}
          </div>
          <h1 className="mt-3 font-black leading-[1.02] tracking-[-0.01em] text-[32px] md:text-[46px] lg:text-[52px]">
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-white/80">
            <span className="inline-flex items-center gap-1.5">
              <UserIcon className="w-3.5 h-3.5" /> {post.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {post.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Cover image */}
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 -mt-8 md:-mt-12 relative z-10">
        <img
          src={post.img}
          alt={post.title}
          className="w-full aspect-[16/9] object-cover rounded-2xl shadow-[0_18px_40px_rgba(15,23,42,0.15)]"
        />
      </div>

      {/* Article body */}
      <article className="max-w-[760px] mx-auto px-6 lg:px-10 py-14 md:py-20" data-testid="blog-post-body">
        {post.body.map(renderBlock)}

        <div className="mt-14 pt-10 border-t border-slate-100 flex items-center justify-between flex-wrap gap-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.22em] text-[#0D5BD7] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> ALL ARTICLES
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[11.5px] font-bold tracking-[0.22em] transition-all shadow-sm hover:shadow-md"
          >
            OUR SERVICES <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#F8F9FB] py-16 md:py-20">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">CONTINUE READING</div>
            <h2 className="mt-3 text-[26px] md:text-[34px] font-black text-[#111827] tracking-[-0.01em]">
              More from our team.
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="group h-full flex flex-col bg-white rounded-2xl border border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.05)] hover:shadow-[0_18px_40px_rgba(13,91,215,0.1)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    <img
                      src={p.img}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-[11px] font-bold tracking-[0.2em] text-[#0D5BD7]">
                      {p.category.toUpperCase()}
                    </div>
                    <h3 className="mt-3 text-[16px] font-black text-[#111827] tracking-tight leading-[1.25] group-hover:text-[#0D5BD7] transition-colors">
                      {p.title}
                    </h3>
                    <div className="mt-4 inline-flex items-center text-[10.5px] font-bold tracking-[0.22em] text-[#0D5BD7]">
                      READ ARTICLE <ArrowRight className="w-3.5 h-3.5 ml-2" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
